"use client";

import { Fragment } from "react";
import { useNoteLabel } from "@/hooks/use-notation";
import {
  BORDER,
  FONT_MONO,
  GOLD,
  GREEN,
  RED,
  TEXT,
  TEXT_DIM,
  TEXT_MUTE,
  VIOLET,
  VIOLET_BRIGHT,
  VIOLET_GLOW,
  VIOLET_SOFT,
} from "./palette";
import {
  formatTempoFeedback,
  HISTORY_LENGTH,
  SLIDE_MS,
  type EventType,
  type LastResult,
  type SlotData,
} from "./tempo-drill-helpers";

type Props = {
  beat: number;
  running: boolean;
  inCountIn: boolean;
  drillActive: boolean;
  countInDone: number;
  drillMeasure: number;
  numNotes: number;
  rootPc: number;
  queue: readonly SlotData[];
  slideOffset: number;
  animating: boolean;
  lastResult: LastResult | null;
  isFlashing: boolean;
  recentEvents: readonly EventType[];
  step: number;
};

export function ChordPracticeTempoStage({
  beat,
  running,
  inCountIn,
  drillActive,
  countInDone,
  drillMeasure,
  numNotes,
  rootPc,
  queue,
  slideOffset,
  animating,
  lastResult,
  isFlashing,
  recentEvents,
  step,
}: Props) {
  return (
    <div
      className="relative z-10 flex flex-1 flex-col items-center justify-center"
      style={
        {
          padding: "20px 40px",
          gap: "clamp(12px, 2.5cqh, 24px)",
          minHeight: 0,
          containerType: "size",
          overflow: "hidden",
        } as React.CSSProperties
      }
    >
      <MetronomeRow
        beat={beat}
        running={running}
        inCountIn={inCountIn}
        drillActive={drillActive}
        countInDone={countInDone}
        drillMeasure={drillMeasure}
      />

      <SlidingQueue
        queue={queue}
        slideOffset={slideOffset}
        animating={animating}
        numNotes={numNotes}
        rootPc={rootPc}
        step={step}
      />

      <FeedbackPill isFlashing={isFlashing} lastResult={lastResult} />

      <HistoryRow recentEvents={recentEvents} />
    </div>
  );
}

type MetronomeRowProps = {
  beat: number;
  running: boolean;
  inCountIn: boolean;
  drillActive: boolean;
  countInDone: number;
  drillMeasure: number;
};

function MetronomeRow({
  beat,
  running,
  inCountIn,
  drillActive,
  countInDone,
  drillMeasure,
}: MetronomeRowProps) {
  let label: string;
  if (inCountIn) {
    label = `Préparation ${countInDone}/4`;
  } else if (drillActive) {
    label = `Mesure ${drillMeasure} · Temps ${beat + 1}/4`;
  } else {
    label = `Prêt, appuie sur démarrer`;
  }

  return (
    <div className="flex items-center" style={{ gap: 16 }}>
      <div className="flex" style={{ gap: 6 }}>
        {[0, 1, 2, 3].map((i) => {
          const lit = running && beat === i;
          const isAccent = i === 0;
          const color = isAccent ? GOLD : VIOLET;
          let boxShadow: string = "none";
          if (lit) {
            boxShadow = isAccent ? `0 0 16px ${GOLD}b3` : `0 0 14px ${VIOLET_GLOW}`;
          }

          return (
            <div
              key={i}
              style={{
                width: 11,
                height: 11,
                borderRadius: 99,
                background: lit ? color : "rgba(255,255,255,0.08)",
                boxShadow,
                transition: "background-color 80ms, box-shadow 80ms",
              }}
            />
          );
        })}
      </div>
      <span
        style={{
          fontFamily: FONT_MONO,
          fontSize: 11,
          color: TEXT_MUTE,
          letterSpacing: "0.2em",
          textTransform: "uppercase",
        }}
      >
        {label}
      </span>
    </div>
  );
}

type SlidingQueueProps = {
  queue: readonly SlotData[];
  slideOffset: number;
  animating: boolean;
  numNotes: number;
  rootPc: number;
  step: number;
};

function SlidingQueue({
  queue,
  slideOffset,
  animating,
  numNotes,
  rootPc,
  step,
}: SlidingQueueProps) {
  const toNoteLabel = useNoteLabel();
  const edgeMask =
    "linear-gradient(to right, transparent 0%, black 10%, black 90%, transparent 100%)";

  return (
    <div
      style={{
        width: "100%",
        maxWidth: 980,
        height: "clamp(140px, 55cqh, 240px)",
        position: "relative",
        overflow: "hidden",
        maskImage: edgeMask,
        WebkitMaskImage: edgeMask,
      }}
    >
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          top: 0,
          bottom: 30,
          left: "50%",
          width: 1,
          borderLeft: `1px dashed ${VIOLET_GLOW}`,
          opacity: 0.4,
          pointerEvents: "none",
        }}
      />

      {queue.map((q, i) => {
        const baseX = (i - 1) * step;
        const effectiveX = baseX + slideOffset;

        let scale = 0.55;
        let opacity = 0.42;
        let label = "";
        let isCurrent = false;
        let blur = 0.4;

        if (Math.abs(effectiveX) < 1) {
          scale = 1;
          opacity = 1;
          label = "▼ MAINTENANT";
          isCurrent = true;
          blur = 0;
        } else if (Math.abs(effectiveX - step) < 1) {
          label = "PROCHAIN";
        } else if (Math.abs(effectiveX - 2 * step) < 1) {
          scale = 0.42;
          opacity = 0.22;
          label = "PUIS";
          blur = 0.6;
        }

        const transition = animating
          ? `transform ${SLIDE_MS}ms cubic-bezier(0.65, 0, 0.35, 1), opacity ${SLIDE_MS}ms cubic-bezier(0.65, 0, 0.35, 1), filter ${SLIDE_MS}ms`
          : "none";

        return (
          <div
            key={i}
            style={{
              position: "absolute",
              bottom: 30,
              left: "50%",
              transform: `translateX(calc(-50% + ${effectiveX}px)) scale(${scale})`,
              opacity,
              transformOrigin: "bottom center",
              filter: blur ? `blur(${blur}px)` : "none",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 10,
              transition,
            }}
          >
            <div
              style={{
                fontFamily: FONT_MONO,
                fontSize: 10,
                color: isCurrent ? VIOLET_BRIGHT : TEXT_MUTE,
                letterSpacing: "0.18em",
                fontWeight: 600,
                height: 14,
                whiteSpace: "nowrap",
              }}
            >
              {label}
            </div>
            <div
              style={{
                fontSize:
                  numNotes > 3
                    ? "min(clamp(48px, 6vw, 110px), 22cqh)"
                    : "min(clamp(56px, 8vw, 140px), 28cqh)",
                fontWeight: 200,
                lineHeight: 0.9,
                letterSpacing: "-0.05em",
                color: isCurrent ? TEXT : TEXT_DIM,
                display: "flex",
                alignItems: "baseline",
                gap: 4,
                whiteSpace: "nowrap",
                fontFamily: "Inter, sans-serif",
              }}
            >
              {q.notes.map((n, ni) => {
                const isRoot = q.pcs[ni] === rootPc;
                const noteStyle: React.CSSProperties = isRoot
                  ? {
                      background: `linear-gradient(180deg, ${VIOLET_BRIGHT} 0%, ${VIOLET} 100%)`,
                      WebkitBackgroundClip: "text",
                      backgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }
                  : {};

                return (
                  <Fragment key={ni}>
                    {ni > 0 && (
                      <span
                        style={{
                          color: TEXT_MUTE,
                          fontSize:
                            numNotes > 3
                              ? "min(clamp(20px, 2.5vw, 46px), 12cqh)"
                              : "min(clamp(28px, 3.5vw, 60px), 14cqh)",
                          margin: "0 8px",
                        }}
                      >
                        ·
                      </span>
                    )}
                    <span style={noteStyle}>{toNoteLabel(n)}</span>
                  </Fragment>
                );
              })}
            </div>
            <div
              style={{
                padding: "3px 10px",
                borderRadius: 4,
                background: isCurrent ? VIOLET_SOFT : "rgba(255,255,255,0.03)",
                border: `1px solid ${isCurrent ? VIOLET_GLOW : BORDER}`,
                fontFamily: FONT_MONO,
                fontSize: 10,
                color: isCurrent ? VIOLET_BRIGHT : TEXT_MUTE,
                letterSpacing: "0.12em",
                fontWeight: 600,
                textTransform: "uppercase",
              }}
            >
              {q.label}
            </div>
          </div>
        );
      })}
    </div>
  );
}

type FeedbackPillProps = {
  isFlashing: boolean;
  lastResult: LastResult | null;
};

function FeedbackPill({ isFlashing, lastResult }: FeedbackPillProps) {
  let bg = "transparent";
  let border = "transparent";
  let color = TEXT_MUTE;

  if (lastResult?.type === "perfect") {
    bg = "rgba(251, 191, 36, 0.15)";
    border = "rgba(251, 191, 36, 0.5)";
    color = GOLD;
  } else if (lastResult?.type === "hit") {
    bg = "rgba(52, 211, 153, 0.12)";
    border = "rgba(52, 211, 153, 0.4)";
    color = GREEN;
  } else if (lastResult?.type === "miss") {
    bg = "rgba(248, 113, 113, 0.12)";
    border = "rgba(248, 113, 113, 0.4)";
    color = RED;
  }

  return (
    <div
      style={{
        height: 30,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {isFlashing && lastResult && (
        <div
          style={{
            padding: "6px 14px",
            borderRadius: 999,
            background: bg,
            border: `1px solid ${border}`,
            fontFamily: FONT_MONO,
            fontSize: 11,
            fontWeight: 600,
            color,
            letterSpacing: "0.15em",
            animation: "tempoFlash 300ms cubic-bezier(0.34, 1.06, 0.5, 1)",
          }}
        >
          {formatTempoFeedback(lastResult)}
        </div>
      )}
    </div>
  );
}

type HistoryRowProps = {
  recentEvents: readonly EventType[];
};

function HistoryRow({ recentEvents }: HistoryRowProps) {
  return (
    <div className="flex items-center" style={{ gap: 5 }}>
      <span
        style={{
          fontFamily: FONT_MONO,
          fontSize: 10,
          color: TEXT_MUTE,
          letterSpacing: "0.15em",
          marginRight: 6,
          textTransform: "uppercase",
        }}
      >
        Historique
      </span>
      {Array.from({ length: HISTORY_LENGTH }).map((_, i) => {
        const ev = recentEvents[recentEvents.length - HISTORY_LENGTH + i];
        let background: string = "rgba(255,255,255,0.05)";
        let boxShadow: string = "none";
        if (ev === "perfect") {
          background = GOLD;
          boxShadow = "0 0 8px rgba(251, 191, 36, 0.5)";
        } else if (ev === "hit") {
          background = GREEN;
        } else if (ev === "miss") {
          background = RED;
        }

        return (
          <div
            key={i}
            style={{
              width: 8,
              height: 22,
              borderRadius: 2,
              background,
              boxShadow,
            }}
          />
        );
      })}
    </div>
  );
}
